import { useCommandState } from 'cmdk';
import { cn } from '@/lib/utils';

export type CommandScope = 'all' | 'pages' | 'actions';

type Props = {
  scope: CommandScope;
  onScopeChange: (scope: CommandScope) => void;
  query: string;
  /** Static counts for the empty-query state — cmdk's own filtered count
   * (below) only reflects what's actually mounted, and on an empty query
   * nothing is filtered out, so these read cleaner than deriving from cmdk. */
  pagesCount: number;
  actionsCount: number;
};

const CHIPS: Array<{ value: CommandScope; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'pages', label: 'Pages' },
  { value: 'actions', label: 'Actions' },
];

/**
 * Sits between `CommandInput` and `CommandList` — filter chips (All / Pages /
 * Actions, narrowing `CommandList` by unmounting the other group entirely —
 * see `CommandPalette.tsx`) plus a small result-count line, from the
 * originally-approved mockup. Must render INSIDE the `Command` tree
 * (a child of `CommandDialog`) — `useCommandState` reads cmdk's own store.
 */
export function CommandResultsBar({ scope, onScopeChange, query, pagesCount, actionsCount }: Props) {
  // Only meaningful once a query narrows the mounted items — see `summary` below.
  const filteredCount = useCommandState((state) => state.filtered.count);
  const isEmptyQuery = query.trim().length === 0;

  const summary = isEmptyQuery
    ? scope === 'pages'
      ? pluralize(pagesCount, 'page')
      : scope === 'actions'
        ? pluralize(actionsCount, 'action')
        : `${pluralize(pagesCount, 'page')} · ${pluralize(actionsCount, 'action')}`
    : pluralize(filteredCount, 'result');

  return (
    <div className="flex items-center justify-between gap-3 border-b border-border px-3 py-2">
      <div className="flex items-center gap-1.5">
        {CHIPS.map((chip) => (
          <button
            key={chip.value}
            type="button"
            onClick={() => onScopeChange(chip.value)}
            aria-pressed={scope === chip.value}
            className={cn(
              'rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
              scope === chip.value
                ? 'bg-[color:var(--brand-50)] text-[color:var(--brand-700)]'
                : 'text-muted-foreground hover:bg-muted',
            )}
          >
            {chip.label}
          </button>
        ))}
      </div>
      <span className="shrink-0 text-xs text-muted-foreground">{summary}</span>
    </div>
  );
}

function pluralize(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}
